import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Description } from '@mui/icons-material';

import {
  useGetWordPluginConfig,
  useSaveWordPluginConfig,
} from '../../hooks/reactQuerys/WordPluginAccessQueries';
import { useGetUsers } from '../../hooks/reactQuerys/UserQueries';
import { useAlert } from '../../context/AlertContext';
import { filterUsersBySearch, toggleUserId } from './userAccessList';

/**
 * Word-Plugin freischalten (#1245) — unter /admin/dev/word-plugin.
 *
 * Urkundenerstellung im Vorgang, „Dokumentvorlagen" und das Word-Add-in sehen
 * nur die hier angehakten Personen, und nur bei eingeschaltetem Hauptschalter.
 * Dasselbe Muster wie die KI-Freischaltung (#1336): leer heißt niemand.
 */
export const WordPluginAccessOverview: React.FC = () => {
  const { showAlert } = useAlert();
  const { wordPluginConfig, isLoadingWordPluginConfig } =
    useGetWordPluginConfig();
  const { saveWordPluginConfig, isPendingSaveWordPluginConfig } =
    useSaveWordPluginConfig();
  const { users, isLoadingUsers } = useGetUsers();

  const [enabled, setEnabled] = useState(false);
  const [enabledUserArray, setEnabledUserArray] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!wordPluginConfig) {
      return;
    }

    setEnabled(wordPluginConfig.Enabled);
    setEnabledUserArray(wordPluginConfig.EnabledUserArray ?? []);
  }, [wordPluginConfig]);

  const visibleUsers = filterUsersBySearch(users ?? [], userSearch);

  async function handleSave() {
    try {
      await saveWordPluginConfig({
        Enabled: enabled,
        EnabledUserArray: enabledUserArray,
      });
      showAlert({
        text: 'Die Freischaltung wurde gespeichert.',
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Die Freischaltung konnte nicht gespeichert werden.',
        severity: 'error',
      });
    }
  }

  if (isLoadingWordPluginConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
        <Description />
        <Typography variant='h5'>Word-Plugin</Typography>
      </Stack>

      <Alert severity='info' sx={{ mb: 3 }}>
        Freigeschaltete Personen sehen im Vorgang „Urkunde erstellen" (in Word
        und in DiNo), als Administrator die „Dokumentvorlagen" und können das
        Word-Add-in benutzen. Alle anderen bemerken davon nichts.
      </Alert>

      <Card variant='outlined' sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEnabled(event.target.checked)
                }
              />
            }
            label='Word-Plugin aktiv'
          />
          <Typography variant='body2' color='text.secondary'>
            Ausgeschaltet sieht niemand das Word-Plugin, auch die unten
            angehakten Personen nicht.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant='subtitle2'>Freigeschaltete Benutzer</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Das Word-Plugin ist ausschließlich für die hier angehakten Personen
            sichtbar.
          </Typography>

          {isLoadingUsers ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  size='small'
                  label='Benutzer suchen'
                  value={userSearch}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setUserSearch(event.target.value)
                  }
                  sx={{ minWidth: 260 }}
                />
                <Typography variant='body2' color='text.secondary'>
                  {enabledUserArray.length} von {(users ?? []).length}{' '}
                  freigeschaltet
                </Typography>
              </Box>

              <Box
                sx={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                {visibleUsers.length === 0 ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ p: 1 }}
                  >
                    Kein Benutzer gefunden.
                  </Typography>
                ) : (
                  visibleUsers.map(user => (
                    <FormControlLabel
                      key={user.idUser}
                      sx={{ display: 'flex', mr: 0 }}
                      control={
                        <Checkbox
                          size='small'
                          checked={enabledUserArray.includes(user.idUser)}
                          onChange={() =>
                            setEnabledUserArray(previous =>
                              toggleUserId(previous, user.idUser)
                            )
                          }
                        />
                      }
                      label={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant='body2'>
                            {user.LastName}, {user.FirstName}
                          </Typography>
                          {Boolean(user.isNotary) && (
                            <Chip size='small' label='Notar' />
                          )}
                          <Typography variant='caption' color='text.secondary'>
                            {user.Email}
                          </Typography>
                        </Box>
                      }
                    />
                  ))
                )}
              </Box>
            </>
          )}

          {enabled && enabledUserArray.length === 0 && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Es ist niemand freigeschaltet — das Word-Plugin ist damit für alle
              Benutzer unsichtbar, obwohl der Hauptschalter an ist.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Button
        variant='contained'
        onClick={() => void handleSave()}
        disabled={isPendingSaveWordPluginConfig}
      >
        {isPendingSaveWordPluginConfig ? 'Wird gespeichert …' : 'Speichern'}
      </Button>
    </Box>
  );
};
